import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/material-icons';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { HashRouter, Switch, Route, Redirect } from 'react-router-dom';
import { initializeStore } from './store/store';
import reducers from 'modules/webClock/reducers/webClockReducers';
import WebClock from './modules/webClock/WebClock';
import WebClockLogin from './modules/webClock/WebClockLogin';
import BootstrapGate from 'components/BootstrapGate';
import SwipeclockTheme from 'components/SwipeclockTheme';
import SnackbarProvider from 'providers/SnackbarProvider';
import { createHashHistory } from 'history';
import { getUrlParameter, isMobileMode } from 'helpers/core/url';
import { bootstrapWebClock } from './webClockBootstrap';
import {
  setWebClockReactRoot,
  WEBCLOCK_FAIL_CLOSED_MESSAGE,
  renderWebClockFailClosed,
} from './webClockFailClosed';
import {
  failClosedAfterUnconfirmedOfflineCleanup as failClosedUnconfirmedCleanup,
  handleWebClockInvalidToken,
} from './modules/webClock/utils/webClockSession';

const store = initializeStore('webClock', reducers);
const history = createHashHistory();

/**
 * Fail-closed after offline cleanup already ran and was not confirmed.
 * Does not re-run cleanup (unlike handleInvalidToken).
 *
 * @param {Error} [error]
 */
export function failClosedAfterUnconfirmedOfflineCleanup(error) {
  failClosedUnconfirmedCleanup({
    dispatch: store.dispatch,
    error,
  });
}

export async function handleInvalidToken() {
  return handleWebClockInvalidToken({
    dispatch: store.dispatch,
    getUser: () => store.getState().user,
    history,
  });
}

export function renderWebClockBootstrapFailure(error) {
  renderWebClockFailClosed(error);
}

export { WEBCLOCK_FAIL_CLOSED_MESSAGE };

function AuthRedirect() {
  if (isMobileMode()) {
    return <Redirect to="/clock/web" />;
  }

  const jwt = getUrlParameter('jwt');
  return jwt ? (
    <Redirect to="/clock/web" />
  ) : (
    <Redirect to="/clock/web/login" />
  );
}

function WebClockLoginRoute() {
  if (isMobileMode()) {
    return <Redirect to="/clock/web" />;
  }

  return <WebClockLogin />;
}

export function WebClockRoutes() {
  return (
    <Switch>
      <Route path="/clock/web/login">
        <WebClockLoginRoute />
      </Route>
      <Route path="/clock">
        <BootstrapGate name="workforcehub" invalidationFn={handleInvalidToken}>
          <WebClock />
        </BootstrapGate>
      </Route>
      <Route exact path="/" component={AuthRedirect} />
    </Switch>
  );
}

function renderApp() {
  const root = ReactDOM.createRoot(document.getElementById('component-root'));
  setWebClockReactRoot(root);
  root.render(
    <React.StrictMode>
      <ReduxProvider store={store}>
        <HashRouter>
          <SwipeclockTheme>
            <SnackbarProvider>
              <WebClockRoutes />
            </SnackbarProvider>
          </SwipeclockTheme>
        </HashRouter>
      </ReduxProvider>
    </React.StrictMode>
  );
}

bootstrapWebClock(renderApp).catch(renderWebClockBootstrapFailure);
